// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React from 'react';
import './DualProgressBar.css';

/**
 * DualProgressBar Component
 * 
 * Displays two progress bars side-by-side for comparison.
 * Useful for showing related metrics like distance vs fuel, health vs enemy health, etc.
 * 
 * @param {Object} props
 * @param {number} props.leftValue - Left bar value (0-100)
 * @param {string} props.leftLabel - Left bar label
 * @param {string} props.leftColor - Left bar color (optional, auto-colors if not provided)
 * @param {number} props.rightValue - Right bar value (0-100)
 * @param {string} props.rightLabel - Right bar label
 * @param {string} props.rightColor - Right bar color (optional, auto-colors if not provided)
 * @param {string} props.size - Size: 'small', 'medium', 'large' (default: 'medium')
 * @param {boolean} props.animated - Enable animation (default: false)
 * @param {boolean} props.showPercentage - Show percentage text (default: true)
 * @param {string} props.className - Additional CSS classes
 */
const DualProgressBar = ({
  leftValue = 0,
  leftLabel = 'Left',
  leftColor,
  rightValue = 0,
  rightLabel = 'Right',
  rightColor,
  size = 'medium',
  animated = false,
  showPercentage = true,
  className = ''
}) => {
  // Clamp values between 0 and 100
  const clampedLeftValue = Math.max(0, Math.min(100, leftValue));
  const clampedRightValue = Math.max(0, Math.min(100, rightValue));

  // Auto color-coding based on value (if no custom color provided)
  const getAutoColor = (value) => {
    if (value >= 70) return '#00ff88'; // green
    if (value >= 40) return '#ffaa00'; // orange
    return '#ff4444'; // red
  };

  const finalLeftColor = leftColor || getAutoColor(clampedLeftValue);
  const finalRightColor = rightColor || getAutoColor(clampedRightValue);

  return (
    <div className={`dual-progress-bar dual-progress-bar-${size} ${className}`}>
      {/* Left Progress Bar */}
      <div className="dual-progress-section">
        <div className="dual-progress-header">
          <span className="dual-progress-label">{leftLabel}</span>
          {showPercentage && (
            <span className="dual-progress-percentage" style={{ color: finalLeftColor }}>
              {Math.round(clampedLeftValue)}%
            </span>
          )}
        </div>
        <div className="dual-progress-bar-container">
          <div
            className={`dual-progress-fill ${animated ? 'dual-progress-animated' : ''}`}
            style={{
              width: `${clampedLeftValue}%`,
              backgroundColor: finalLeftColor,
              boxShadow: `0 0 10px ${finalLeftColor}`
            }}
          />
        </div>
      </div>

      {/* Divider */}
      <div className="dual-progress-divider">
        <span className="dual-progress-vs">VS</span>
      </div>

      {/* Right Progress Bar */}
      <div className="dual-progress-section">
        <div className="dual-progress-header">
          <span className="dual-progress-label">{rightLabel}</span>
          {showPercentage && (
            <span className="dual-progress-percentage" style={{ color: finalRightColor }}>
              {Math.round(clampedRightValue)}%
            </span>
          )}
        </div>
        <div className="dual-progress-bar-container">
          <div
            className={`dual-progress-fill ${animated ? 'dual-progress-animated' : ''}`}
            style={{
              width: `${clampedRightValue}%`,
              backgroundColor: finalRightColor,
              boxShadow: `0 0 10px ${finalRightColor}`
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default DualProgressBar;
